import type { BillingProfileView } from '@/api/billing/profile'

/**
 * Whether the billing identity is complete enough for the top-up to stop asking for it.
 *
 * **Derived here, because the contract carries no such flag.** `BillingProfileView` publishes no
 * `is_complete` (Django's profile did); what it does publish is `customer_id`, `null` until a
 * profile was saved or a top-up created the customer. So a profile is complete when a customer
 * exists and it carries what the identity form requires: a name, a country, the company name
 * when the party is a company — the invoicing service refuses a company without one — and an
 * address, which an invoice names and whose postal code ledger's tax resolution reads.
 *
 * A customer created by a first top-up from the organization's name (the backend's
 * `identity_for`) carries no address, so it fails that test and the step is asked once more.
 */
export function isBillingProfileComplete(profile: BillingProfileView | null | undefined): boolean {
  if (!profile || profile.customer_id == null) return false
  if (!profile.name?.trim() || !profile.country) return false
  if (profile.kind === 'company' && !profile.company_name?.trim()) return false
  const address = (profile.address ?? {}) as { line1?: string | null; postal_code?: string | null; city?: string | null }
  return [address.line1, address.postal_code, address.city].every((part) => !!part?.trim())
}
